import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "@/components/site/Logo";
import { COMPANY } from "@/lib/brands";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/brands", label: "Brands" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-parchment/85 backdrop-blur-xl border-b border-aubergine/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14">
        <div className="flex h-20 items-center justify-between">
          <Link
            to="/"
            data-testid="navbar-logo-link"
            className="flex items-center gap-3.5 group"
          >
            <Logo className="h-10 w-10 md:h-11 md:w-11" />
            <div className="leading-none">
              <div className="font-display font-medium text-[20px] md:text-[22px] tracking-[0.02em] uppercase text-aubergine">
                RSA OVERSEAS
              </div>
              <div className="mt-1.5 text-[9.5px] tracking-[0.32em] uppercase text-aubergine/60">
                <span className="font-bold text-aubergine">PRIVATE LIMITED</span>
                <span className="mx-1.5 text-magenta">·</span>
                <span className="font-semibold">EST. {COMPANY.est}</span>
              </div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `relative px-5 py-2 text-[13px] tracking-[0.02em] font-medium transition-colors ${
                    isActive
                      ? "text-magenta"
                      : "text-aubergine/75 hover:text-aubergine"
                  }`
                }
              >
                {({ isActive }) => (
                  <span className="flex items-center gap-2">
                    {isActive && (
                      <span className="h-1 w-1 rounded-full bg-magenta" />
                    )}
                    {item.label}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-6">
            <a
              href={`tel:${COMPANY.phoneRaw}`}
              data-testid="navbar-phone-link"
              className="text-[13px] text-aubergine/70 hover:text-aubergine transition-colors font-medium"
            >
              {COMPANY.phone}
            </a>
            <Link
              to="/contact"
              data-testid="navbar-cta-button"
              className="group inline-flex items-center gap-2 bg-aubergine hover:bg-magenta text-parchment text-[12px] tracking-[0.16em] uppercase font-semibold px-6 py-3.5 transition-colors duration-300"
            >
              Enquire
              <span className="h-1 w-4 bg-parchment transition-all group-hover:w-6" />
            </Link>
          </div>

          <button
            data-testid="navbar-mobile-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden p-2 text-aubergine"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="navbar-mobile-menu"
          className="md:hidden bg-parchment border-t border-aubergine/10"
        >
          <div className="px-6 py-6 flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `font-display py-3 text-2xl tracking-tight ${
                    isActive ? "text-magenta" : "text-aubergine"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-cta-button"
              className="mt-4 inline-flex items-center justify-center gap-2 bg-aubergine text-parchment text-[12px] tracking-[0.18em] uppercase font-semibold px-6 py-4"
            >
              Enquire
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
